import type {ItemShopProduct, ArchonPack, VoucherDetails} from "@/types/commerce";
import type {Transform} from "@/types/common";
import type {Quest, QuestCategory, QuestMapping} from "@/types/quests";
import type {WikiContent, WikiGroups} from "@/types/wiki";
import type {ServerListRecord, SpiritualGuideSetting} from "@/types/settings";
import type {DiscordMessage} from "@/types/discord";
import type {StandaloneAttack} from "@/types/entities";
import type {UUID, SettingResponse, CreatedGroups} from "@/types/common";
import type {ToastHandle} from "@/types/toast";
import type {ExtractedTranslation, FieldTranslation, TranslationGroupsResponse, TranslationObjectsResponse, TranslationFieldResponse} from "@/types/translations";
import type {ItemPrototype} from "@/types/items";
import type {Mob, NPC, Spawner} from "@/types/entities";
import type {GameItemPreset} from "@/types/game-items";
import type {BaseVocationStats, ConvoTimeouts, AITokenLimits, AITokenLimitsException, NpcSharedKnowledge, NetworkRegion, SkillsSetting} from "@/types/settings";
import axios from "axios";
import {errorUtil} from "@/utils/ErrorUtil";
import {toastInstance} from "@/components/toast/Tostify";
import {mapDynamicTranslationUpdate, updateTranslationsIds} from "@/mappers/translation/TranslationMappers";
import {TranslationType} from "@/type/TranslationType";

export const HttpMethod = {
    GET: 'get',
    POST: 'post',
    PATCH: 'patch',
    PUT: 'put',
    DELETE: 'delete'
} as const;
export type HttpMethodValue = typeof HttpMethod[keyof typeof HttpMethod];

/**
 * Validate a collection of objects and optionally perform custom validation.
 *
 * @param {Object|Array} validationObjs - An object or an array of objects to validate.
 * @param {Object} toast - An object used to manage and display feedback messages.
 * @param {string} errorMessage - The message to display in case of validation errors.
 * @param {Function|null} [customValidation=null] - Optional function for custom validation logic. Should return true if the object is valid.
 * @param {boolean} [returnInvalidObjs=false] - If true, returns the array of invalid objects instead of a boolean.
 *
 * @returns {Promise<boolean|Array>} - Returns true if all objects are valid, or an array of invalid objects if `returnInvalidObjs` is true.
 *
 * @example Basic validation with feedback
 * const toast = this.$toast.loading('Checking data...');
 * if (!await validate(this.v$.group.name, toast, 'Please select a group')) {
 *   // Handle failed validation
 * }
 *
 * @example Validation with custom logic and handling invalid objects
 * const validationResults = this.$refs.entityDetails.map(ref => ref.v$.item);
 * const validationResult = await validate(validationResults, toast, "", null, true);
 *
 * if (Array.isArray(validationResult)) {
 *   this.invalidIndices = validationResult;
 *   return;
 * }
 */
export async function validate<T extends {$touch(): void; $invalid: boolean}>(validationObjs: T | T[], toast: ToastHandle, errorMessage: string, customValidation: ((value: T) => boolean) | null = null, returnInvalidObjs = false) {
    if (!Array.isArray(validationObjs)) {
        validationObjs = [validationObjs];
    }

    let invalidObjsWithIndices: { obj: T; index: number; }[] = [];

    validationObjs.forEach((validationObj, index) => {
        let isValid = true;

        if (customValidation) {
            isValid = customValidation(validationObj);
        } else {
            validationObj.$touch();
            isValid = !validationObj.$invalid;
        }

        if (!isValid) {
            invalidObjsWithIndices.push({obj: validationObj, index});
        }
    });

    if (invalidObjsWithIndices.length > 0) {
        toastInstance.update(toast.id, {description: errorMessage, type: 'error'});

        const firstInputErrorEl = document.querySelector<HTMLElement>('.error-input-field');
        const firstMultiselectErrorEl = document.querySelector<HTMLElement>('.error-multiselect-field');

        const firstErrorEl = firstInputErrorEl || firstMultiselectErrorEl;

        if (firstErrorEl) {
            firstErrorEl.scrollIntoView({behavior: 'smooth', block: 'center'});
            firstErrorEl.focus();
        }

        return returnInvalidObjs ? invalidObjsWithIndices.map(({index}) => index) : false;
    }

    return true;
}

export function validateTranslations(extractedTranslations: ExtractedTranslation[], toast: ToastHandle) {
    const invalidFields = extractedTranslations.filter(t =>
        !t.optional && !t.translated_texts.some((tt: { language: string; text: string; }) => tt.language === 'en' && tt.text?.trim())
    );

    if (invalidFields.length > 0) {
        let errorMessage = 'Invalid translation fields found. \nPlease ensure each item has an <#>"en"</#> translation.\n';
        errorMessage += 'Invalid fields:\n';

        invalidFields.forEach(({fuid, special_field_type}) => {
            errorMessage += `- <#>${special_field_type || '??'}</#> (${fuid})\n`;
        });

        toastInstance.update(toast.id, {
            description: errorMessage,
            type: 'error'
        });

        console.debug('Invalid translations (missing "en"):', invalidFields);

        const firstInvalid = invalidFields[0];
        if (firstInvalid?.fuid) {
            const el = document.querySelector(`[data-fuid="${firstInvalid.fuid}"]`);
            if (el) {
                el.scrollIntoView({behavior: 'smooth', block: 'center'});
            }
        }

        return false;
    }

    return true;
}

/**
 * Sends an HTTP request using Axios.
 *
 * @param {'get'|'post'|'patch'|'put'|'delete'} method - HTTP method to use (e.g., HttpMethod.GET).
 * @param {String} url - The URL to send the request to.
 * @param {Object|Array} [data={}] - Data to send with the request (only for non-GET methods).
 * @param {Object|null} [toast=null] - Toast object used to show a success message.
 * @param {String} [successMessage=''] - Message to display on success.
 *
 * @returns {Promise<boolean|Array>} - Returns response data if successful, `true` if no data, or `false` if the request fails.
 */
type RequestResult<T> = T extends object ? T : Exclude<T, undefined | null | false | 0 | ''> | true;

export async function performRequest<TResponse = unknown>(method: HttpMethodValue, url: string, data: unknown = {}, toast: ToastHandle | null = null, successMessage = ''): Promise<RequestResult<TResponse>> {
    try {
        const config = {
            method: method.toLowerCase(),
            url,
            ...(method.toUpperCase() === HttpMethod.GET ? {} : {data})
        };

        const response = await axios<TResponse>(config);

        if (toast) {
            toastInstance.update(toast.id, {
                description: successMessage,
                type: 'success'
            });
        }

        return (response.data || true) as RequestResult<TResponse>;
    } catch (error) {
        await errorUtil.catchErrors(error, toast);
        throw error;
    }
}

export function generateWrapUpdatePayload<K extends string, T>(wrapKey: K, updatePayload: T): Record<K, T> {
    return {
        [wrapKey]: updatePayload
    } as Record<K, T>
}

export function generateUpdatePayload<T, TResult, TKey extends string = "id">(items: T[], oldItems: Partial<NoInfer<T>>[], optionsMapper: (item: NoInfer<T>, oldItem: Partial<NoInfer<T>>) => TResult, idExtractor: (item: T) => UUID, extractors: Record<string, (item: T) => unknown> = {}, idKey: TKey = 'id' as TKey) {
    return items.map(item => {
        const id = idExtractor(item);
        const oldItem = oldItems.find(old => idExtractor(old as T) === id);

        const newData = oldItem
            ? optionsMapper(item, oldItem)
            : optionsMapper(item, {});

        const additionalData: Record<string, unknown> = {};
        for (const [key, extractor] of Object.entries(extractors)) {
            additionalData[key] = extractor(item);
        }

        return {
            [idKey]: id,
            ...additionalData,
            new_data: newData
        } as Record<TKey, UUID> & {new_data: TResult} & Record<string, unknown>;
    });
}

export function generateCreatePayload<T, TResult>(groupName: string, items: T[], optionsMapper: (item: T) => TResult, extraDataKey: string | Record<string, never> | null = null, extraData: unknown = null) {
    const groupPayload = {[groupName]: items.map(item => optionsMapper(item))};

    if (isNull(extraDataKey) && isNull(extraData)) {
        return groupPayload;
    } else if (isEmptyObject(extraDataKey) && isEmptyObject(extraData)) {
        return {groups: groupPayload};
    } else {
        return {[extraDataKey as string]: extraData, groups: groupPayload};
    }
}

function isEmptyObject(obj: unknown) {
    return Object.keys(obj as object).length === 0 && (obj as object).constructor === Object;
}

function isNull(obj: unknown) {
    return obj === null || obj === undefined;
}

export async function performCopyRequest(confirmModal: {openModal(message: string): Promise<boolean>} | null, toast: ToastHandle, copyData: {data: unknown; translations?: ExtractedTranslation[]; translationType?: import("@/types/translations").TranslationCategory}, url: string, confirmMessage: string, groupId: UUID, isCopy = true) {
    let isConfirmed = false;

    if (confirmModal) {
        isConfirmed = await confirmModal.openModal(confirmMessage);

        if (!isConfirmed) {
            toastInstance.update(toast.id, {
                description: 'Action cancelled',
                type: 'info'
            })
            return false;
        }
    }

    const {data: mapperData, translations: translationsData} = copyData;

    const responseData = await performRequest<CreatedGroups>(HttpMethod.POST, url, mapperData, toast, 'Copy completed successfully');

    if (!translationsData) return true

    if (!responseData) {
        console.error('No UIDs returned from copy request:', responseData);
        return false;
    }

    const translationToast = toastInstance.loading("Copying translations...");
    const translationPayload = mapDynamicTranslationUpdate(translationsData, groupId, isCopy)
    const updatedTranslations = updateTranslationsIds(translationPayload, responseData.groups, translationToast);

    return await performRequest(HttpMethod.PUT, `/translations/dynamic/${copyData.translationType ?? TranslationType.OTHER}`, updatedTranslations, translationToast, 'Copy translations successfully');
}

/**
 * Save or update a group of items.
 *
 * @param {Array} items - Current items.
 * @param {Array} oldItems - Previous items.
 * @param {boolean} editingGroup - True if editing a group.
 * @param {Object} group - Group data to process.
 * @param {Object} toast - An object used to manage and display feedback messages.
 * @param {Function} generateUpdatePayload - Builds update data.
 * @param {Function} generateCreatePayload - Builds creation data.
 * @param {Object} urls - Object specifying URL endpoints.
 *                        - `url`: Base URL for both operations.
 *                        - `patchUrl`: URL for patch requests, defaults to `url` if not provided.
 *                        - `postUrl`: URL for post requests, also defaults to `url` if not provided.
 * @param {Function} comparator - Compares old and new items.
 *
 * @returns {Promise<boolean>}
 *
 * @example
 * const toast = this.$toast.loading('Checking data...');
 *
 * await saveOrUpdateGroup(
 *   this.items,
 *   this.oldItems,
 *   this.editingGroup,
 *   this.group,
 *   toast,
 *   (updateItems, oldItems) => generateUpdatePayload(updateItems, oldItems, mapNpcUpdateOptions, item => item.id),
 *   (groupName, createItems) => generateCreatePayload(groupName, createItems, mapNpcCreateOptions, "meta", {
 *     location: this.location.location,
 *     map: this.location.map
 *   }),
 *   { url: '/entities/npcs' },
 *   (oldItem, newItem) => (oldItem as {id?: UUID}).id === (newItem as {id?: UUID}).id
 * );
 *
 */
export async function saveOrUpdateGroup<T>(items: T[], oldItems: T[], editingGroup: boolean, group: {name?: string}, toast: ToastHandle, generateUpdatePayload: (items: T[], oldItems: T[]) => unknown, generateCreatePayload: (groupName: string, items: T[]) => unknown, {
        url = '',
        patchUrl = url,
        postUrl = url,
        wrapUpdatePayloadKey = ''
    }: {url?: string; patchUrl?: string; postUrl?: string; wrapUpdatePayloadKey?: string}, comparator: (oldItem: T, newItem: T) => boolean = (oldItem, newItem) => (oldItem as {id?: UUID}).id === (newItem as {id?: UUID}).id) {
    const updateItems: T[] = [];
    const createItems: T[] = [];

    items.forEach(newItem => {
        if (oldItems.some(oldItem => comparator(oldItem, newItem))) {
            updateItems.push(newItem);
        } else {
            createItems.push(newItem);
        }
    });

    let responseData = null;

    if (editingGroup) {
        if (updateItems.length) {
            const updatePayload = wrapUpdatePayloadKey !== ''
                ? generateWrapUpdatePayload(wrapUpdatePayloadKey, generateUpdatePayload(updateItems, oldItems))
                : generateUpdatePayload(updateItems, oldItems)

            responseData = await performRequest<import("@/types/common").CreatedGroups>(HttpMethod.PATCH, patchUrl, updatePayload, toast, 'Items updated successfully');
            return responseData;
        }

        if (createItems.length) {
            const createPayload = generateCreatePayload(group.name!, createItems);
            responseData = await performRequest<import("@/types/common").CreatedGroups>(HttpMethod.POST, postUrl, createPayload, toast, 'Items saved successfully')
            return responseData;
        }
    } else {
        const createPayload = generateCreatePayload(group.name!, items);
        responseData = await performRequest<import("@/types/common").CreatedGroups>(HttpMethod.POST, postUrl, createPayload, toast, 'Items saved successfully');
        return responseData;
    }

    return true;
}

/**
 * Formats one or many items by loading their translations and applying a custom format function.
 *
 * @param {Object|Array} input - Single item object or array of items to format.
 * @param {string} translationType - Type of translation to load (e.g. 'NPCS').
 * @param {string|null} groupId - Translation group ID, used only for multiple items.
 * @param {Function} formatItemFn - Function(item, translations) => formatted item.
 * @param {Function} [idExtractor=item => item.id] - Function to extract the ID from an item.
 *
 * @returns {Promise<Object|Array|null>} - Formatted item or array of formatted items, or null if input empty.
 */
export function formatItemsWithTranslations<T, TResult>(input: T[], translationType: string, groupId: UUID | null, formatItemFn: (item: T, translations: FieldTranslation[]) => TResult | Promise<TResult>, idExtractor?: (item: T) => UUID): Promise<TResult[]>;
export function formatItemsWithTranslations<T extends object, TResult>(input: T, translationType: string, groupId: UUID | null, formatItemFn: (item: T, translations: FieldTranslation[]) => TResult | Promise<TResult>, idExtractor?: (item: T) => UUID): Promise<TResult>;
export function formatItemsWithTranslations<T, TResult>(input: T | null, translationType: string, groupId: UUID | null, formatItemFn: (item: T, translations: FieldTranslation[]) => TResult | Promise<TResult>, idExtractor?: (item: T) => UUID): Promise<TResult | null>;
export function formatItemsWithTranslations<T, TResult>(input: T | T[] | null, translationType: string, groupId: UUID | null, formatItemFn: (item: T, translations: FieldTranslation[]) => TResult | Promise<TResult>, idExtractor?: (item: T) => UUID): Promise<TResult | TResult[] | null>;
export async function formatItemsWithTranslations<T, TResult>(input: T | T[] | null, translationType: string, groupId: UUID | null, formatItemFn: (item: T, translations: FieldTranslation[]) => TResult | Promise<TResult>, idExtractor: (item: T) => UUID = item => (item as {id: UUID}).id) {
    const isArray = Array.isArray(input);

    if (!input || (isArray && input.length === 0)) {
        return isArray ? [] : null;
    }

    if (isArray) {
        const translationObjects = await loadTranslationGroupObjects(translationType, groupId!);

        const translationsMap: Record<UUID, FieldTranslation[]> = {};
        translationObjects.forEach(({object_uid, fields_translations}) => {
            translationsMap[object_uid] = fields_translations;
        });

        return Promise.all(
            input.map(item => {
                const itemId = idExtractor(item);

                const translations = translationsMap[itemId] || [];
                return formatItemFn(item, translations);
            })
        );
    }

    const itemId = idExtractor(input);

    const translations = await loadTranslationObject(translationType, itemId);
    return formatItemFn(input, translations);
}

export async function applyGroupTranslations<T, TChild>({
        entities,
        groupIds,
        translationType,
        getGroupId,
        getEntityId,
        getChildren,
        setEntityTranslation
    }: {entities: T[]; groupIds: UUID[]; translationType: string; getGroupId: (entity: T) => UUID; getEntityId: (child: TChild) => UUID; getChildren: (entity: T) => TChild[]; setEntityTranslation: (child: TChild, name: string) => void}) {
    let translations: TranslationGroupsResponse["groups"] = [];

    try {
        const response = await axios.get<TranslationGroupsResponse>(`/translations/dynamic/${translationType}/groups?groups=${groupIds.join(',')}`);
        translations = response.data.groups;
    } catch (error) {
        await errorUtil.catchErrors(error);
        return null;
    }

    const translationsMap = new Map<UUID, Map<UUID, string>>();
    for (const group of translations) {
        const innerMap = new Map<UUID, string>();
        for (const obj of group.objects) {
            innerMap.set(obj.id, obj.name);
        }
        translationsMap.set(group.group_id, innerMap);
    }

    for (const entity of entities) {
        const groupId = getGroupId(entity);
        const groupMap = translationsMap.get(groupId);
        const children = getChildren(entity);

        for (const child of children) {
            const translatedName = groupMap?.get(getEntityId(child)) || "[ ‼ ] TRANSLATION NOT FOUND [ ‼ ]";
            setEntityTranslation(child, translatedName);
        }
    }

    return entities;
}

export async function getItemById<TItem extends ItemPrototype = ItemPrototype>(itemId: string) {
    const responseData = await performRequest<TItem[]>(
        HttpMethod.GET,
        `/items/${itemId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No item found for ID: ${itemId}`);
        return null;
    }

    return responseData[0];
}

export async function getItemByNormalizedId<TItem extends ItemPrototype = ItemPrototype>(normalizedId: string) {
    const responseData = await performRequest<TItem[]>(
        HttpMethod.GET,
        `/items/by-normalized-id/${normalizedId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No item found for normalizedId: ${normalizedId}`);
        return null;
    }

    return responseData[0];
}

export async function getItemShopProductById(productId: string) {
    const responseData = await performRequest<ItemShopProduct[]>(
        HttpMethod.GET,
        `/itemshop/products/${productId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No item shop product found for ID: ${productId}`);
        return null;
    }

    return responseData[0];
}

export async function getMobById(mobId: string) {
    const responseData = await performRequest<Mob[]>(
        HttpMethod.GET,
        `/entities/mobs/${mobId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No mob found for ID: ${mobId}`);
        return null;
    }

    return responseData[0];
}

export async function getMobByNormalizedId(normalizedId: string) {
    const responseData = await performRequest<Mob[]>(
        HttpMethod.GET,
        `/entities/mobs/by-normalized-id/${normalizedId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No mob found for normalized ID: ${normalizedId}`);
        return null;
    }

    return responseData[0];
}

export async function getSpawnerById(spawnerId: string) {
    const responseData = await performRequest<Spawner[]>(
        HttpMethod.GET,
        `/entities/spawners/${spawnerId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No spawner found for ID: ${spawnerId}`);
        return null;
    }

    return responseData[0];
}

export async function getSpawnerByNormalizedId(normalizedId: string) {
    const responseData = await performRequest<Spawner[]>(
        HttpMethod.GET,
        `/entities/spawners/by-normalized-id/${normalizedId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No spawner found for normalized ID: ${normalizedId}`);
        return null;
    }

    return responseData[0];
}

export async function getNpcById(npcId: string) {
    const responseData = await performRequest<NPC[]>(
        HttpMethod.GET,
        `/entities/npcs/${npcId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No NPC found for ID: ${npcId}`);
        return null;
    }

    return responseData[0];
}

export async function getNpcByNormalizedId(normalizedId: string) {
    const responseData = await performRequest<NPC[]>(
        HttpMethod.GET,
        `/entities/npcs/by-normalized-id/${normalizedId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No NPC found for normalized ID: ${normalizedId}`);
        return null;
    }

    return responseData[0];
}

export async function getQuestCategoryById(categoryId: string) {
    const responseData = await performRequest<QuestCategory[]>(
        HttpMethod.GET,
        `/quests/categories/${categoryId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No quest category found for ID: ${categoryId}`);
        return null;
    }

    return responseData[0];
}

export async function getQuestById(questId: string) {
    const responseData = await performRequest<Quest[]>(
        HttpMethod.GET,
        `/quests/${questId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No quest found for ID: ${questId}`);
        return null;
    }

    return responseData[0];
}

export async function getQuestMappingById(mappingId: string) {
    const responseData = await performRequest<QuestMapping[]>(
        HttpMethod.GET,
        `/quests/mappings/${mappingId}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No quest mapping found for ID: ${mappingId}`);
        return null;
    }

    return responseData[0];
}

export async function getWikiContent(categoryId: string) {
    const responseData = await performRequest<WikiContent>(
        HttpMethod.GET,
        `/wiki/content/${categoryId}`
    );

    if (!responseData) {
        console.error(`No wiki content found for category ID: ${categoryId}`);
        return null;
    }

    return responseData;
}

export async function getWikiGroups(contentType: string, countryCode: string) {
    const responseData = await performRequest<WikiGroups>(
        HttpMethod.GET,
        `/wiki/content/${contentType}/${countryCode}/groups`
    );

    if (!responseData) {
        console.error(`No wiki groups found for content type: ${contentType}, country code: ${countryCode}`);
        return null;
    }

    return responseData;
}

export async function getBaseVocationStats() {
    const responseData = await performRequest<SettingResponse<BaseVocationStats>>(
        HttpMethod.GET,
        `/settings/base-vocation-stats`
    );

    if (!responseData || !responseData.setting) {
        console.error('No base vocation stats found');
        return null;
    }

    return responseData.setting;
}

export async function getConvoTimeouts() {
    const responseData = await performRequest<SettingResponse<ConvoTimeouts>>(
        HttpMethod.GET,
        `/settings/convo-timeouts`
    );

    if (!responseData || !responseData.setting) {
        console.error('No convo timeouts found');
        return null;
    }

    return responseData.setting;
}

export async function getNetworkRegion(regionId: string) {
    const responseData = await performRequest<{region: NetworkRegion}>(
        HttpMethod.GET,
        `/settings/network-infra/regions/${regionId}`
    );

    if (!responseData) {
        console.error(`No network region found for ID: ${regionId}`);
        return null;
    }

    return responseData;
}

export async function getGameItem(gameItemId: string) {
    const responseData = await performRequest<GameItemPreset>(
        HttpMethod.GET,
        `/game-items/${gameItemId}`
    );

    if (!responseData) {
        console.error(`No game item found for ID: ${gameItemId}`);
        return null;
    }

    return responseData;
}

export async function loadTranslationObject(translationType: string, itemId: string) {
    const responseData = await performRequest<TranslationObjectsResponse>(
        HttpMethod.GET,
        `/translations/dynamic/${translationType}/objects/${itemId}`
    );

    const firstObject = responseData?.objects?.[0];

    if (!firstObject || !firstObject.fields_translations) {
        console.error(`No fields found for translation type: ${translationType}, itemId: ${itemId}`);
        return [];
    }

    return firstObject.fields_translations;
}

export async function loadTranslationGroupObjects(translationType: string, groupId: string) {
    const responseData = await performRequest<TranslationObjectsResponse>(
        HttpMethod.GET,
        `/translations/dynamic/${translationType}/groups/${groupId}/objects`
    );

    if (!responseData || !responseData.objects) {
        console.error(`No objects found for translation type: ${translationType}, groupId: ${groupId}`);
        return [];
    }

    return responseData.objects;
}

export async function loadTranslationObjectFiled(translationType: string, itemId: string, fieldId: string) {
    const responseData = await performRequest<TranslationFieldResponse>(
        HttpMethod.GET,
        `/translations/dynamic/${translationType}/objects/${itemId}/fields/${fieldId}`
    );

    if (!responseData || !responseData.fields_translations) {
        console.error(`No fields found for translation type: ${translationType}, itemId: ${itemId}, fieldId: ${fieldId}`);
        return [];
    }

    return responseData.fields_translations;
}

export async function getPlayerPosition(playerName: string) {
    const responseData = await performRequest<Transform[]>(
        HttpMethod.GET,
        `/entities/players//${playerName}/transform`
    );

    if (!responseData) {
        console.error(`No position found for player: ${playerName}`);
        return null;
    }

    return responseData[0];
}

export async function getNpcSharedKnowledge() {
    const responseData = await performRequest<SettingResponse<NpcSharedKnowledge>>(
        HttpMethod.GET,
        `/settings/npc-shared-knowledge`
    );

    if (!responseData || !responseData.setting) {
        console.error('No shared knowledge found');
        return null;
    }

    return responseData.setting.knowledge;
}

export async function getAITokensLimits() {
    const responseData = await performRequest<SettingResponse<AITokenLimits>>(
        HttpMethod.GET,
        `/settings/ai-tokens-limits`
    );

    if (!responseData || !responseData.setting) {
        console.error('No shared knowledge found');
        return null;
    }

    return responseData.setting;
}

export async function getAITokensLimitsExceptions(id: string) {
    const responseData = await performRequest<AITokenLimitsException[]>(
        HttpMethod.GET,
        `/settings/ai-tokens-limits/exceptions/${id}`
    );

    if (!responseData || responseData.length === 0) {
        console.error(`No exception found for ID: ${id}`);
        return null;
    }

    return responseData[0];
}

export async function getSkills() {
    const responseData = await performRequest<SettingResponse<SkillsSetting>>(
        HttpMethod.GET,
        `/settings/skills`
    );

    if (!responseData || !responseData.setting) {
        console.error('No skills setting found');
        return null;
    }

    return responseData.setting.skill_configs;
}

export async function getServerList() {
    const responseData = await performRequest<{servers: ServerListRecord[]}>(
        HttpMethod.GET,
        `/server-control/list`
    );

    if (!responseData || !responseData.servers) {
        console.error('No servers found');
        return null;
    }

    return responseData.servers
}

export async function getDiscordMessageById(id: string) {
    const responseData = await performRequest<DiscordMessage>(
        HttpMethod.GET,
        `/discord/message/${id}`
    );

    if (!responseData) {
        console.error(`No discord item found for ID: ${id}`);
        return null;
    }

    return responseData;
}

export async function getSpiritualGuideSettings() {
    const responseData = await performRequest<SettingResponse<SpiritualGuideSetting>>(
        HttpMethod.GET,
        `/settings/spiritual-guide`
    );

    if (!responseData || !responseData.setting) {
        console.error(`No spiritual guide settings found`);
        return null;
    }

    return responseData.setting;
}

export async function getArchonPack(id: string) {
    const responseData = await performRequest<ArchonPack[]>(
        HttpMethod.GET,
        `/itemshop/archon-packs/${id}`
    );

    if (!responseData) {
        console.error(`No archon pack item found for ID: ${id}`);
        return null;
    }

    return responseData[0];
}

export async function getAttack(id: string) {
    const responseData = await performRequest<{attacks: StandaloneAttack[]}>(
        HttpMethod.GET,
        `/entities/attacks/${id}`
    );

    if (!responseData.attacks[0]) {
        console.error(`No attack found for ID: ${id}`);
        return null;
    }

    return responseData.attacks[0];
}

export async function getVoucherDetails(code: string) {
    const responseData = await performRequest<VoucherDetails>(
        HttpMethod.GET,
        `/itemshop/vouchers/${code}`
    );

    if (!responseData) {
        console.error(`No voucher found for code: ${code}`);
        return null;
    }

    return responseData;
}